import { useMemo, useState, type CSSProperties } from "react";
import { Button, Loader } from "@mantine/core";
import { CustomerOperations, CustomerSession } from "./types";
import { FilterChips, type FilterChip } from "../FilterChips";

// Inline styles (not Tailwind): these views ship inside the robotics bundle,
// which does NOT include Tailwind — only Mantine (host-provided) + inline CSS.
const c = {
  slate400: "#94a3b8",
  slate500: "#64748b",
  slate800: "#1e293b",
  slate900: "#0f172a",
  border: "#e2e8f0",
  white: "#ffffff",
  greenText: "#059669",
  blueText: "#2563eb",
  amberText: "#b45309",
  amberBg: "#fffbeb",
  amberBorder: "#fcd34d",
  amberPillBg: "#fef3c7",
  amberPillText: "#92400e",
  orangeBg: "#fff7ed",
  orangeBorder: "#fdba74",
  orangePillBg: "#ffedd5",
  orangePillText: "#9a3412",
};

interface CustomerOperationsViewProps {
  data?: CustomerOperations;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  locationLabel?: string;
  onReturnUnit?: (session: CustomerSession) => void;
  returningSessionId?: number | null;
}

export function CustomerOperationsView({
  data,
  isLoading,
  isError,
  error,
  locationLabel,
  onReturnUnit,
  returningSessionId,
}: CustomerOperationsViewProps) {
  const [statusFilter, setStatusFilter] = useState<SessionFilter>("All");

  const filtered = useMemo(
    () =>
      (data?.sessions ?? []).filter(
        (s) => statusFilter === "All" || sessionCategory(s) === statusFilter
      ),
    [data?.sessions, statusFilter]
  );

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 320 }}>
        <Loader />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: 24 }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: "#991b1b", marginBottom: 8 }}>Error Loading Sessions</div>
        <div style={{ color: "#b91c1c" }}>
          {error instanceof Error ? error.message : "Something went wrong."}
        </div>
      </div>
    );
  }

  const { sessions } = data;

  return (
    <div style={{ width: "100%" }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: c.slate400, marginBottom: 4 }}>
          Customer Operations — {locationLabel ?? "—"}
        </div>
        <div style={{ color: c.slate500 }}>
          Everyone currently checking in, checking out, or waiting for a unit. Sessions taking
          longer than expected are flagged automatically.
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <FilterChips
          chips={buildSessionChips(sessions)}
          active={statusFilter}
          onChange={(v) => setStatusFilter(v as SessionFilter)}
        />
      </div>

      {sessions.length === 0 ? (
        <div style={{ background: c.white, border: `1px solid ${c.border}`, borderRadius: 12, padding: 40, textAlign: "center", color: c.slate500 }}>
          No active sessions right now.
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ background: c.white, border: `1px solid ${c.border}`, borderRadius: 12, padding: 40, textAlign: "center", color: c.slate500 }}>
          No sessions are currently “{statusFilter}”.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filtered.map((session) => (
            <SessionRow
              key={session.id}
              session={session}
              onReturnUnit={onReturnUnit}
              isReturning={returningSessionId === session.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const SESSION_FILTERS = ["All", "On Track", "Stalled", "Malfunction"] as const;
type SessionFilter = (typeof SESSION_FILTERS)[number];

function sessionCategory(session: CustomerSession): Exclude<SessionFilter, "All"> {
  if (session.flag === "Stalled") return "Stalled";
  if (session.flag === "Malfunction") return "Malfunction";
  return "On Track";
}

const SESSION_CHIP_COLORS: Record<Exclude<SessionFilter, "All">, FilterChip["colors"]> = {
  "On Track": { bg: "#ecfdf5", border: "#6ee7b7", text: c.greenText },
  Stalled: { bg: c.amberBg, border: c.amberBorder, text: c.amberText },
  Malfunction: { bg: c.orangeBg, border: c.orangeBorder, text: c.orangePillText },
};

function buildSessionChips(sessions: CustomerSession[]): FilterChip[] {
  const counts: Record<string, number> = {};
  for (const s of sessions) counts[sessionCategory(s)] = (counts[sessionCategory(s)] ?? 0) + 1;
  return SESSION_FILTERS.map((f) => ({
    value: f,
    label: f,
    count: f === "All" ? sessions.length : counts[f] ?? 0,
    colors: f === "All" ? undefined : SESSION_CHIP_COLORS[f],
  }));
}

function SessionRow({
  session,
  onReturnUnit,
  isReturning,
}: {
  session: CustomerSession;
  onReturnUnit?: (session: CustomerSession) => void;
  isReturning: boolean;
}) {
  const stalled = session.flag === "Stalled";
  const malfunction = session.flag === "Malfunction";
  const canReturn = !!onReturnUnit && session.unitId != null;

  const bg = malfunction ? c.orangeBg : stalled ? c.amberBg : c.white;
  const borderColor = malfunction ? c.orangeBorder : stalled ? c.amberBorder : c.border;

  const miniLabel: CSSProperties = { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: c.slate400 };

  return (
    <div
      style={{
        borderRadius: 12,
        border: `1px solid ${borderColor}`,
        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
        padding: "16px 20px",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 16,
        background: bg,
      }}
    >
      <div style={{ minWidth: 160 }}>
        <div style={miniLabel}>Customer</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: c.slate900 }}>{session.customerName}</div>
        <div style={{ fontSize: 12, color: c.slate400 }}>
          {[
            session.unitNumber ? `Unit ${session.unitNumber}` : null,
            session.platformNumber ? `Platform ${session.platformNumber}` : null,
          ]
            .filter(Boolean)
            .join(" · ") || "—"}
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={miniLabel}>Current Step</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: c.slate900 }}>{session.currentStep}</div>
        {stalled && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 4, background: c.amberPillBg, color: c.amberPillText, fontSize: 12, fontWeight: 500, padding: "2px 8px", borderRadius: 6 }}>
            ⚠ Stalled — exceeds expected duration
          </span>
        )}
        {malfunction && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 4, background: c.orangePillBg, color: c.orangePillText, fontSize: 12, fontWeight: 500, padding: "2px 8px", borderRadius: 6 }}>
            ✕ Malfunction reported
          </span>
        )}
      </div>

      <div style={{ minWidth: 110 }}>
        <div style={miniLabel}>Wait Time</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: c.slate900 }}>{session.waitMinutes} min</div>
        <div style={{ fontSize: 12, color: c.slate400 }}>Target: {session.targetMinutes} min</div>
      </div>

      {canReturn && (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Button size="xs" variant="light" color="primary" loading={isReturning} onClick={() => onReturnUnit!(session)}>
            Return Unit
          </Button>
        </div>
      )}
    </div>
  );
}
